import React, { useId, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';

// `size` do `<input>` nativo (número de caracteres) não faz sentido num
// checkbox e é omitido para o nome ficar livre para a densidade de toque.
export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'size'> {
  /** Label text displayed next to the checkbox */
  label?: string;
  /** Description text below the label */
  description?: string;
  /** Controlled checked state */
  checked?: boolean;
  /** Change handler */
  onChange?: (checked: boolean) => void;
  /** Indeterminate state (for select-all patterns) */
  indeterminate?: boolean;
  /** Error message */
  error?: string;
  /**
   * Densidade do alvo de toque. `touch` estica a linha inteira para ≥44px
   * (iOS HIG) e faz o rótulo ocupar toda a altura — o alvo clicável passa a ser
   * a linha, não só o quadrado de 18px. Para telas usadas no celular, em campo
   * (ex.: preenchimento de checklist). `default` mantém EXATAMENTE a densidade
   * histórica.
   */
  size?: 'default' | 'touch';
}

/** Altura mínima da linha no modo `touch` (iOS HIG ≥44px). */
const TOUCH_MIN_HEIGHT = 44;

/**
 * Shared Checkbox component — visual custom do DS (`.cbox`): quadrado com check
 * preenchido na cor primária. Mantém um `<input type="checkbox">` real (sr-only)
 * para acessibilidade/teclado/forms; o quadrado visível reflete o estado.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label="Aceitar termos"
 *   description="Ao marcar, você concorda com os termos de uso"
 *   checked={accepted}
 *   onChange={setAccepted}
 * />
 * ```
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      checked,
      onChange,
      indeterminate = false,
      error,
      className,
      id: propId,
      disabled,
      size = 'default',
      ...props
    },
    forwardedRef
  ) => {
    const touch = size === 'touch';
    const generatedId = useId();
    const inputId = propId || generatedId;
    const internalRef = useRef<HTMLInputElement | null>(null);

    // Handle indeterminate state (can only be set via JS)
    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const setRefs = (el: HTMLInputElement | null) => {
      internalRef.current = el;
      if (typeof forwardedRef === 'function') {
        forwardedRef(el);
      } else if (forwardedRef) {
        forwardedRef.current = el;
      }
    };

    const active = !!checked || indeterminate;

    return (
      <div
        className={clsx('flex gap-2.5', touch ? 'items-center' : 'items-start', className)}
        style={touch ? { minHeight: TOUCH_MIN_HEIGHT } : undefined}
      >
        <label
          htmlFor={inputId}
          className={clsx('relative flex-none', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
          style={{ lineHeight: 0, ...(touch ? {} : { marginTop: 1 }) }}
        >
          <input
            ref={setRefs}
            type="checkbox"
            id={inputId}
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
            className="peer sr-only"
            {...props}
          />
          <span
            className="flex items-center justify-center transition-colors peer-focus-visible:shadow-[0_0_0_3px_var(--ring-color)]"
            style={{
              width: 18,
              height: 18,
              borderRadius: 5,
              border: active ? 'none' : '1.6px solid var(--theme-input-border)',
              backgroundColor: active ? 'var(--theme-primary)' : 'var(--theme-input-background)',
              color: '#ffffff',
            }}
          >
            {indeterminate ? (
              <span style={{ width: 9, height: 2, borderRadius: 1, backgroundColor: '#ffffff' }} />
            ) : checked ? (
              <Icon name="check" className="w-3 h-3" />
            ) : null}
          </span>
        </label>
        {(label || description || error) && (
          <div className="flex-1 min-w-0">
            {label && (
              <label
                htmlFor={inputId}
                className={clsx(
                  'text-sm font-medium select-none',
                  // `touch`: o rótulo vira bloco de altura cheia — clicar em
                  // qualquer ponto da linha marca a opção.
                  touch && 'flex items-center',
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                )}
                style={{ color: 'var(--theme-text)', ...(touch ? { minHeight: TOUCH_MIN_HEIGHT } : {}) }}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                {description}
              </p>
            )}
            {error && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-error-color)' }} role="alert">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
